import {useState} from "react";
import {useFieldArray, UseFieldArrayReturn, useFormContext, useWatch} from "react-hook-form";
import {ChevronDown, ChevronRight, CircleAlert, WandSparklesIcon, X} from "lucide-react";
import {FormField, FormItem, FormMessage} from "@/components/ui/form.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import SelectInputField from "@/components/forms/SelectInputField.tsx";
import TextInputField from "@/components/forms/TextInputField.tsx";
import SpellSearchView from "@/characters/admin/form/SpellSearchView.tsx";
import UseFieldArrayOptions from "@/components/forms/UseFieldArrayOptions.tsx";
import {CharacterFormInput} from "@/characters/admin/form/CharacterFormSchema.ts";
import {useModal} from "@/modal/ModalHooks.ts";
import {displaySpellLevel} from "@/spells/SpellFormatting.ts";
import DeleteConfirm from "@/components/forms/DeleteConfirm.tsx";
import {SpellSummary} from "@/spells/model/Spell.ts";
import {srcUrl} from "@/api/ImageApi.ts";

export default function SpellGroupItemView({groupIndex, spellGroupArray}: {
    groupIndex: number;
    spellGroupArray: UseFieldArrayReturn<CharacterFormInput, "spellcasting.spellGroups">;
}) {
    const {control, formState, getFieldState} = useFormContext<CharacterFormInput>();
    const spellsArray = useFieldArray({
        control,
        name: `spellcasting.spellGroups.${groupIndex}.spells`,
        keyName: "spellKey",
    });
    const type = useWatch({control, name: "spellcasting.type"});
    const group = useWatch({control, name: `spellcasting.spellGroups.${groupIndex}`});
    const level = group && "level" in group ? group.level : 0;
    const slots = group && "slots" in group ? group.slots : 0;
    const frequency = group && "frequency" in group ? group.frequency : "";
    const [expanded, setExpanded] = useState(spellsArray.fields.length === 0);
    const {showSpell} = useModal();
    const spellcastingErrors = formState.errors.spellcasting as {spellGroups?: unknown[]} | undefined;
    const hasError = Boolean(
        getFieldState(`spellcasting.spellGroups.${groupIndex}`, formState).error ||
        spellcastingErrors?.spellGroups?.[groupIndex],
    );
    const detailsOpen = expanded || hasError;

    const addSpell = (spell: SpellSummary) => {
        if (!spellsArray.fields.some(existing => existing.id === spell.id)) spellsArray.append(spell);
    };

    const title = type === "Klasse"
        ? displaySpellLevel(Number(level))
        : frequency || "Neue Häufigkeit";
    const details = type === "Klasse"
        ? `${slots} ${Number(slots) === 1 ? "Platz" : "Plätze"}`
        : "Angeboren";

    return <section className="overflow-hidden rounded-lg border bg-background">
        <header className="flex flex-wrap items-center gap-1.5 p-1.5">
            <Button type="button" variant="ghost" size="icon-sm" onClick={() => setExpanded(value => !value)}
                    aria-label={detailsOpen ? "Zaubergruppe einklappen" : "Zaubergruppe aufklappen"}
                    aria-expanded={detailsOpen}>
                {detailsOpen ? <ChevronDown/> : <ChevronRight/>}
            </Button>
            <span className="min-w-32 grow">
                <span className="font-medium">{title}</span>
                <span className="ml-1.5 text-xs text-muted-foreground">· {details}</span>
            </span>
            <Badge variant="outline" className="text-[11px]">
                {spellsArray.fields.length} Zauber
            </Badge>
            {hasError && <CircleAlert className="size-4 text-destructive" aria-label="Zaubergruppe enthält Fehler"/>}
            <SpellSearchView level={type === "Klasse" ? Number(level) : undefined} addSpell={addSpell}/>
            <UseFieldArrayOptions index={groupIndex} array={spellGroupArray}/>
        </header>
        {detailsOpen && <div className="border-t p-3">
            <div className="max-w-xl">
                {type === "Klasse" && (
                    <FormField name={`spellcasting.spellGroups.${groupIndex}`} render={() => (
                        <div className="grid gap-3 sm:grid-cols-2">
                            <SelectInputField name={`spellcasting.spellGroups.${groupIndex}.level`} title="Grad"
                                              defaultValue="0" options={LEVELS}/>
                            <SelectInputField name={`spellcasting.spellGroups.${groupIndex}.slots`} title="Plätze"
                                              defaultValue="0" options={SLOTS}/>
                            <FormMessage/>
                        </div>
                    )}/>
                )}
                {type === "Angeboren" && (
                    <TextInputField name={`spellcasting.spellGroups.${groupIndex}.frequency`} title="Häufigkeit"
                                    placeholder="z. B. 1/Tag" className="sm:max-w-72"/>
                )}
            </div>
        </div>}
        <FormField name={`spellcasting.spellGroups.${groupIndex}.spells`} render={() => (
            <FormItem className="gap-0 border-t">
                    <div className="flex min-h-9 flex-wrap items-center gap-1.5 px-2 py-1.5">
                        {spellsArray.fields.length === 0 && (
                            <span className="text-sm text-muted-foreground">Noch keine Zauber ausgewählt</span>
                        )}
                        {spellsArray.fields.map((spell, index) => (
                            <Badge key={spell.spellKey} variant="secondary" className="rounded-xs gap-1 py-0.5 pl-0.5 pr-1">
                                <button type="button" className="flex min-w-0 items-center gap-1.5 rounded-xs pr-1 hover:underline"
                                        onClick={() => showSpell(spell.id)}>
                                    <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-xs border bg-white">
                                        {spell.image
                                            ? <img src={srcUrl(spell.image)} alt="" className="size-full object-contain"/>
                                            : <WandSparklesIcon className="size-3 text-muted-foreground"/>}
                                    </span>
                                    <span className="truncate text-sm">{spell.name}</span>
                                </button>
                                <DeleteConfirm title={`${spell.name} entfernen?`}
                                               description="Der Zauber wird aus dieser Zaubergruppe entfernt. Der globale Zauber bleibt erhalten."
                                               onConfirm={() => spellsArray.remove(index)}>
                                    <button type="button"
                                            className="rounded-full text-muted-foreground hover:bg-destructive/15 hover:text-destructive"
                                            aria-label={`${spell.name} entfernen`}>
                                        <X className="size-4"/>
                                    </button>
                                </DeleteConfirm>
                            </Badge>
                        ))}
                    </div>
                    <FormMessage/>
            </FormItem>
        )}/>
    </section>;
}

const LEVELS = Object.fromEntries(Array.from({length: 10}, (_, level) => [level, displaySpellLevel(level)]));
const SLOTS = Array.from({length: 10}).map((_, slots) => String(slots));
